/**
 * Tensors that are **in the file but are not parameters**.
 *
 * ## The defect this exists for
 *
 * `openai-community/gpt2` reported **137M parameters** against a real
 * 124,439,808. The gap was exactly `12 × 1024 × 1024 = 12,582,912` — the twelve
 * `h.N.attn.bias` causal masks, `1 × 1 × 1024 × 1024` of F32, one per layer,
 * counted as weights. Reported by a visitor on 2026-08-17 (report `79b5cce5`),
 * and the number had been wrong since the tool could read gpt2 at all.
 *
 * A causal mask is a lower-triangular constant. It is not learned, it is not
 * trained, it does not move, and PyTorch does not count it — it is a
 * `register_buffer`, which is precisely the distinction "how many parameters
 * does this model have" turns on. Every checkpoint that ships one had the same
 * defect in proportion to how much of it was buffer; gpt2's 10.1% is the worst
 * ratio because its context window is square and its model is small.
 *
 * ## What a buffer does to each of the three headline figures
 *
 * The convention (owner ruling, 2026-08-17, on the reviewer's recommendation):
 *
 * - **params** counts learned parameters only. Buffers are excluded.
 * - **total bytes** stays the whole file, buffers included, because that is the
 *   one figure a visitor can check independently against the Hub.
 * - **bits/weight** is *weight* bytes × 8 ÷ params — the true per-weight cost.
 *
 * The rejected alternative is worth writing down because it looks right: drop
 * the mask's elements from `params` and leave its bytes in the total, and gpt2
 * reads **35.24** bits/weight for a checkpoint that is F32 throughout. That is
 * not a per-weight cost at all — it is weight bytes and mask bytes divided by
 * weights — and it would have replaced a wrong number with a wrong number that
 * is harder to argue with. The three figures move together or they lie; see
 * `anatomyTotals`, which is where the identity is enforced.
 *
 * ## Why classification is name + shape, and not the RMS check
 *
 * The proof that gpt2's `attn.bias` is a mask rather than a bias vector is
 * statistical: **Σ(RMS) 0.7075**, and a lower-triangular 0/1 mask over an
 * `N × N` grid has RMS `√0.5005 = 0.7075`. That is conclusive, and it is
 * deliberately **not** used here.
 *
 * Everything in this module runs against `anatomy`, which is built from headers
 * alone — the page says "metadata analysis · no weights fetched" and the share
 * card has that sentence rendered into it. An RMS check needs the payload, so
 * using it to classify would mean fetching every buffer candidate on page load:
 * a prefetch nobody asked for, against the "no crawling, no prefetch, no
 * background refresh" invariant, to decide a question the header already
 * answers.
 *
 * The header answers it because **shape is corroboration**. A learned bias is a
 * vector — one dimension, matching its layer's width. A causal mask is
 * `1 × 1 × N × N`: four dimensions, square in the last two. Nothing that is
 * actually a bias has that shape, so name and shape together are decisive
 * without reading a byte.
 *
 * The RMS check keeps its job, one layer up: the tensor detail pane already
 * fetches the payload *because the visitor asked it to*, so that is where a
 * buffer can be corroborated rather than merely classified. See
 * `bufferCorroboration`.
 */

import type { InventoryTensor } from "../safetensors/inventory.js";

/** What kind of non-parameter a tensor is, once something has matched. */
export type BufferKind =
  | "causal-mask"
  | "attention-constant"
  | "rope-frequencies"
  | "position-ids";

export interface BufferVerdict {
  readonly kind: BufferKind;
  /** Human-readable, for the placement disclosure. Mirrors `rule` on a role. */
  readonly rule: string;
  /**
   * The role to file this under, when the name table's answer is wrong.
   * `undefined` means keep whatever role was already chosen.
   */
  readonly role: string | undefined;
}

/**
 * A rule is a name pattern **and** a shape predicate, never a name alone.
 *
 * Both halves are load-bearing and for different reasons. The name is what
 * makes the rule specific to a known buffer; the shape is what stops a
 * plausible name from silently removing real parameters from the count.
 *
 * Getting this wrong is worse than the defect it fixes. An over-eager rule
 * *under*-reports a model — quietly, in the direction a reader is least likely
 * to question, since a smaller number looks like the tool being careful. So the
 * shape predicate is required on every rule, including the ones where the name
 * looks unambiguous.
 */
interface BufferRule {
  readonly kind: BufferKind;
  readonly rule: string;
  readonly pattern: RegExp;
  readonly shapeOk: (shape: readonly number[]) => boolean;
  /**
   * Replace the role the name table chose, when that role is *wrong* rather
   * than merely unaware.
   *
   * "Is this a parameter" and "where does this sit in the architecture" are two
   * axes, and conflating them is the mistake this field exists to avoid. Most
   * buffers keep whatever role they had: `rope_freqs` is already filed under
   * `rope`, which is accurate, specific, and more useful to a reader than a
   * generic bucket — flagging it as a buffer changes what it contributes to the
   * totals and should not also relocate it on the map.
   *
   * The causal mask is the exception, and it is a real one. The name table
   * matches it on `\bbias\b` and files 48 MiB of lower-triangular ones under
   * `bias`, beside 55 KiB of genuine bias vectors — so the row claims gpt2 has
   * 48 MiB of biases, which is false about the model rather than merely
   * imprecise. That role is corrected here because there is nowhere else that
   * knows the shape, and the shape is what proves it is not a bias.
   */
  readonly role?: string;
}

const RULES: readonly BufferRule[] = [
  {
    kind: "causal-mask",
    rule: "causal mask (register_buffer)",
    // See `role` on BufferRule: `bias` is wrong about this tensor, not merely
    // vague, and it is the byte-heavy one that made the row misleading.
    role: "buffer",
    /*
     * The segment immediately before `.bias` must be exactly `attn` or
     * `attention`.
     *
     * This is the whole reason the pattern is anchored rather than a `\bbias\b`
     * search. GPT-2's real biases are `attn.c_attn.bias`, `attn.c_proj.bias`,
     * `mlp.c_fc.bias`, `mlp.c_proj.bias` — every one of them ends in `.bias`
     * and sits under `attn.` or `mlp.`, and a loose pattern would delete
     * 12 × 4,608 genuine parameters while fixing the mask.
     */
    pattern: /(?:^|\.)(?:attn|attention)\.bias$/,
    /*
     * `1 × 1 × N × N`, N > 1.
     *
     * A bias vector is one-dimensional. Four dimensions, square in the last
     * two, with two leading broadcast axes is a mask over (query, key)
     * positions and cannot be anything else. `N > 1` rejects the degenerate
     * `1×1×1×1`, which would be a scalar wearing a mask's shape.
     */
    shapeOk: (s) => {
      const [a, b, rows, cols] = s;
      return (
        s.length === 4 &&
        a === 1 &&
        b === 1 &&
        rows !== undefined &&
        rows > 1 &&
        rows === cols
      );
    },
  },
  {
    kind: "attention-constant",
    rule: "masked_bias constant (register_buffer)",
    // Same reasoning as the mask: it matches `\bbias\b` and is not a bias.
    role: "buffer",
    // GPT-2's `-1e4` fill value. A single number, stored as a tensor.
    pattern: /(?:^|\.)masked_bias$/,
    // Scalar: safetensors spells that as an empty shape, and `numel` is 1.
    shapeOk: (s) => s.length === 0 || (s.length === 1 && s[0] === 1),
  },
  {
    kind: "rope-frequencies",
    rule: "rotary frequencies (register_buffer)",
    /*
     * `inv_freq` under any of the module names that have carried it. Present in
     * older Llama-family exports; newer ones compute it at load time and ship
     * nothing, which is why this is absent from most modern checkpoints rather
     * than universal.
     */
    pattern: /(?:^|\.)(?:rotary_emb|rotary_pos_emb|rope)\.inv_freq$/,
    // A vector of half the head dimension. One dimension, and non-empty.
    shapeOk: (s) => s.length === 1 && (s[0] ?? 0) > 0,
  },
  {
    kind: "rope-frequencies",
    rule: "rope frequencies (GGUF)",
    // llama.cpp's spelling of the same thing.
    pattern: /(?:^|\.)rope_freqs(?:\.weight)?$/,
    shapeOk: (s) => s.length === 1 && (s[0] ?? 0) > 0,
  },
  {
    kind: "position-ids",
    rule: "position ids (register_buffer)",
    // An `arange`, shipped by some exports. Never learned.
    pattern: /(?:^|\.)position_ids$/,
    shapeOk: (s) => s.length <= 2 && s.every((d) => d > 0),
  },
];

/**
 * Is this physical tensor a non-parameter buffer?
 *
 * Returns the verdict rather than a boolean so the placement disclosure can say
 * *which* rule fired — the same reason `Classification` carries `rule`. A
 * reader who disagrees with an exclusion needs to see what decided it, since
 * this is the one classification that changes a headline number downward.
 */
export function classifyBuffer(
  tensor: Pick<InventoryTensor, "name" | "shape">,
): BufferVerdict | undefined {
  for (const rule of RULES) {
    if (rule.pattern.test(tensor.name) && rule.shapeOk(tensor.shape)) {
      return { kind: rule.kind, rule: rule.rule, role: rule.role };
    }
  }
  return undefined;
}

/**
 * What a buffer's decoded values should look like, for the tensor detail pane.
 *
 * The classification above never reads a byte. But once a visitor opens a
 * tensor, the payload is fetched anyway — and at that point a *prediction* the
 * header could not make becomes checkable. This returns the expected statistic
 * and the reasoning, so the pane can corroborate rather than restate.
 *
 * Only the causal mask gets a prediction, because it is the only one whose
 * contents are fully determined by its shape: a lower-triangular fill of ones
 * over `N × N` has mean `(N+1)/2N` and, being 0/1, RMS `√mean`. For N = 1024
 * that is `√0.50049 = 0.70745` — which is what gpt2's `h.0.attn.bias` measures,
 * and is how this whole class of defect was proven rather than argued.
 *
 * `undefined` for the rest: an `inv_freq` vector's values depend on the base
 * frequency and `position_ids` is an `arange`, so neither has a single expected
 * statistic worth asserting. Saying nothing is better than a loose range that
 * would pass on anything.
 */
export function bufferCorroboration(
  verdict: BufferVerdict,
  shape: readonly number[],
): { readonly expectedRms: number; readonly why: string } | undefined {
  if (verdict.kind !== "causal-mask") return undefined;
  const n = shape[3];
  if (n === undefined || n <= 1) return undefined;
  // Lower-triangular including the diagonal: N(N+1)/2 ones out of N².
  const mean = (n + 1) / (2 * n);
  return {
    expectedRms: Math.sqrt(mean),
    why: `a lower-triangular 0/1 mask over ${n}×${n} has RMS √${mean.toFixed(5)}`,
  };
}
